import React from 'react'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AlertCircle, Loader2 } from 'lucide-react'

// ADD OR EDIT ONE PAYMENT METHOD (docs/plans/2026-09-09-billing-payers.md §9.1).
//
// Two fields, because the backend takes two: what the operator calls this payment method, and one
// optional address its invoices are COPIED to. Since §10.1 the notices themselves go to the people
// on the business who can act on them, so this field is a mailbox for an accountant or an AP
// system — hence "also", and hence optional. The card itself is never typed here — it is vaulted
// through the Stripe rail from the block's own "Add card" action.
//
// ONE SHAPE, TWO INTENTS. Adding sends both fields; editing sends only what actually changed, so
// an untouched invoice email is left alone rather than re-written (the backend distinguishes
// "not sent" from "sent empty", and sent-empty is the legitimate way to clear an address).
//
// EVERY REFUSAL SENTENCE IS THE SERVER'S. The 422 body carries one finished sentence and it is
// rendered verbatim: a business on trial, a name already in use, anything later. This component
// composes no billing sentence of its own.

export interface PaymentMethodFields {
  name: string
  billingEmail: string | null
}

export default function PaymentMethodFormDialog({
  open,
  mode,
  initial = null,
  saving,
  error,
  onOpenChange,
  onSubmit,
}: {
  open: boolean
  mode: 'add' | 'edit'
  /** The row being edited, so the fields open pre-filled. Null when adding. */
  initial?: PaymentMethodFields | null
  saving: boolean
  /** The server's sentence, rendered verbatim. */
  error: string | null
  onOpenChange: (open: boolean) => void
  onSubmit: (body: Record<string, string>) => void
}) {
  const [name, setName] = React.useState('')
  const [email, setEmail] = React.useState('')

  // Re-seeded every time the dialog opens, and on whichever row opened it — a second Edit click
  // on a different block must not inherit the first one's typing.
  React.useEffect(() => {
    if (!open) return
    setName(initial?.name ?? '')
    setEmail(initial?.billingEmail ?? '')
  }, [open, initial?.name, initial?.billingEmail])

  const isEdit = mode === 'edit'
  const trimmedName = name.trim()
  const trimmedEmail = email.trim()
  const nameChanged = trimmedName !== (initial?.name ?? '')
  const emailChanged = trimmedEmail !== (initial?.billingEmail ?? '')
  const canSubmit = saving ? false : isEdit ? trimmedName !== '' && (nameChanged || emailChanged) : trimmedName !== ''

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return

    if (!isEdit) {
      onSubmit({ name: trimmedName, billing_email: trimmedEmail })
      return
    }

    // Only what moved. An omitted field is untouched; an empty one is a deliberate clear.
    const body: Record<string, string> = {}
    if (nameChanged) body.name = trimmedName
    if (emailChanged) body.billing_email = trimmedEmail
    onSubmit(body)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit payment method' : 'Add a payment method'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Rename it, or change the address its invoices are also sent to.'
              : 'Name the company that pays. You can choose which hotels it pays for once it is added.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
          <DialogBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="payment-method-name">Name</Label>
              <Input
                id="payment-method-name"
                type="text"
                autoComplete="off"
                placeholder="Harbour Annex Ltd"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Use one payment method for each company that pays.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="payment-method-email">
                Invoice email <span className="font-normal text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="payment-method-email"
                type="email"
                autoComplete="off"
                placeholder="finance@harbourannex.co.uk"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Invoices and payment notices are also sent to this address.
              </p>
            </div>

            {error && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              className="rounded-lg"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" className="rounded-lg" disabled={!canSubmit} aria-busy={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  {isEdit ? 'Saving…' : 'Adding…'}
                </>
              ) : (
                <>{isEdit ? 'Save changes' : 'Add payment method'}</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
